import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Search, ArrowRight, ChevronLeft, ChevronRight, Star, Shield, ShieldCheck,
  Globe, MapPin, Users, Award, Sparkles, ClipboardList, Target, BarChart3,
  Scissors, Smile, Eye, Activity, Heart, TrendingUp, CheckCircle, X, Mail,
  Loader2
} from 'lucide-react';
import { useLanguage, getLocalizedField } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import api, { corporateIdentityAPI, apiErrorMessage } from '../../utils/api';
import { useContent } from '../../hooks/useContent';
import { LegalFooter } from '../../components/LegalFooter';
import CorporateCertificates from '../../components/CorporateCertificates';
import SEO from '../../components/SEO';
import { useHomepageSeoProps } from '../homepageSeo';


// ═══════════════════════════════════════════════════════════
// STATIC FALLBACKS & CONSTANTS
// ═══════════════════════════════════════════════════════════

const GRADIENT_COLORS = [
  'from-teal-500 to-emerald-600', 'from-blue-500 to-indigo-600',
  'from-violet-500 to-purple-600', 'from-rose-500 to-pink-600',
  'from-amber-500 to-orange-600',
];

const TREATMENT_COLORS = [
  'bg-teal-50 text-teal-600 border-teal-100', 'bg-blue-50 text-blue-600 border-blue-100',
  'bg-violet-50 text-violet-600 border-violet-100', 'bg-rose-50 text-rose-600 border-rose-100',
  'bg-amber-50 text-amber-600 border-amber-100', 'bg-emerald-50 text-emerald-600 border-emerald-100',
  'bg-sky-50 text-sky-600 border-sky-100', 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100',
];

const ICON_MAP = { Scissors, Smile, Eye, Sparkles, Activity, Heart, Star, Target, BarChart3, Award, Shield };

const HOW_IT_WORKS = [
  { step_number: 1, title: 'Tell us what you need', description: 'Select your treatment and answer a few health questions', Icon: ClipboardList },
  { step_number: 2, title: 'Get matched', description: 'Our AI matches you with verified clinics that fit your needs', Icon: Target },
  { step_number: 3, title: 'Compare offers', description: 'Receive personalized offers and compare clinics side-by-side', Icon: BarChart3 },
  { step_number: 4, title: 'Book with confidence', description: 'Secure payment, aftercare support, and full transparency', Icon: ShieldCheck },
];

const TESTIMONIALS = [
  { name: 'Sarah M.', country: 'UK', treatment: 'Hair Transplant', quote: 'The entire process was seamless. TreatX matched me with an incredible clinic in Istanbul. My results exceeded expectations.', rating: 5 },
  { name: 'Michael R.', country: 'Germany', treatment: 'Dental Implants', quote: 'Saved 60% compared to local prices. The clinic in Budapest was world-class. Cannot recommend TreatX enough.', rating: 5 },
  { name: 'Lisa K.', country: 'USA', treatment: 'Rhinoplasty', quote: 'I was nervous about surgery abroad, but TreatX verified everything. The aftercare coordination was exceptional.', rating: 5 },
];

const TRUST_FALLBACK = [
  { title: 'Verified Clinics Only', description: 'Every clinic undergoes thorough credential verification and quality assessment before listing.', icon: 'ShieldCheck' },
  { title: 'Transparent Pricing', description: 'No hidden fees. Get detailed offers with full cost breakdowns before making any decisions.', icon: 'BarChart3' },
  { title: 'Patient Protection', description: '', icon: 'Shield', cmsKey: 'trust_patient_protection_text' },
];

const CERT_FALLBACK = [];  // Retired. Public certificates are the exclusive
// authority of `treatx_corporate_certificates` (canonical CMS). Hard-coded
// fallback badges (JCI / ISO 9001 / TEMOS) would constitute a second SSOT
// and were removed as part of the Slice 2 closure. If the canonical
// endpoint is unreachable, the Homepage renders NO certificates rather
// than a fabricated set.

const TRUST_ICON_MAP = { ShieldCheck, BarChart3, Shield, Award, Star, CheckCircle };

// Loading skeleton
const SectionSkeleton = ({ dark }) => (
  <div className={`py-12 ${dark ? 'bg-[#0a1628]' : 'bg-white'}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 animate-pulse">
      <div className={`h-6 w-48 ${dark ? 'bg-white/10' : 'bg-slate-200'} rounded mb-2 mx-auto`} />
      <div className={`h-4 w-72 ${dark ? 'bg-white/5' : 'bg-slate-100'} rounded mb-8 mx-auto`} />
      <div className="grid md:grid-cols-3 gap-4">
        {[0, 1, 2].map(i => (
          <div key={i} className={`h-40 ${dark ? 'bg-white/5' : 'bg-slate-100'} rounded-xl`} />
        ))}
      </div>
    </div>
  </div>
);


// ═══════════════════════════════════════════════════════════
// SECTION COMPONENTS
// ═══════════════════════════════════════════════════════════

// 1. Hero Section with Search
// TREATX-SSOT-PART4 — treatments/categories come from the canonical
// Master Medical Catalog. Selecting a category filters the visible
// treatment options. Provider enablement is applied on /find-clinics,
// never in this selector. A user may search by category alone (any
// treatment), by treatment alone, or by both.
const HeroSearch = ({ metrics, treatments, categories, countries, navigate }) => {
  const [selCategory, setSelCategory] = useState('');
  const [selTreatment, setSelTreatment] = useState('');
  const [selCountry, setSelCountry] = useState('');
  const [selBudget, setSelBudget] = useState('');

  const filteredTreatments = React.useMemo(() => {
    if (!selCategory) return treatments;
    return treatments.filter(t => (t.category || t.category_slug) === selCategory);
  }, [treatments, selCategory]);

  // If the current treatment falls outside the filtered set after a
  // category change, reset it.
  React.useEffect(() => {
    if (selTreatment && !filteredTreatments.some(t => (t.slug || t.treatment_id) === selTreatment)) {
      setSelTreatment('');
    }
  }, [filteredTreatments, selTreatment]);

  const onExplore = () => {
    const params = new URLSearchParams();
    if (selCategory)  params.set('category', selCategory);
    if (selTreatment) params.set('treatment', selTreatment);
    if (selCountry)   params.set('country', selCountry);
    if (selBudget)    params.set('budget', selBudget);
    const qs = params.toString();
    navigate(qs ? `/find-clinics?${qs}` : '/find-clinics');
  };

  const catName = (c) => {
    const n = c.name;
    return typeof n === 'object' ? (n?.en || c.slug) : (n || c.slug);
  };
  const trtName = (t) => {
    const n = t.name ?? t.treatment_name;
    return typeof n === 'object' ? (n?.en || '') : (n || '');
  };

  return (
    <section className="relative overflow-hidden bg-[#0a1628] text-white" data-testid="hero-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#3BA4C5]/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 mb-5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-slate-300">{(metrics.total_patients || 0).toLocaleString()}+ patients treated worldwide</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.1] tracking-tight mb-4">
              World-Class Care,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3BA4C5] to-[#62d4f5]">Your Terms.</span>
            </h1>
            <p className="text-base text-slate-400 max-w-md mb-8 leading-relaxed">
              Compare verified clinics across {metrics.total_countries || 30} countries. Get personalized offers. Save up to 70%.
            </p>
            <div className="flex gap-8" data-testid="hero-stats">
              {[
                { val: `${metrics.total_clinics || 200}+`, label: 'Verified Clinics' },
                { val: `${metrics.total_countries || 30}+`, label: 'Countries' },
                { val: `${metrics.total_patients || 5000}+`, label: 'Patients Served' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-2xl font-bold text-white">{s.val}</div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6" data-testid="hero-search-card">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Search className="h-4 w-4 text-[#3BA4C5]" /> Find Your Treatment
            </h3>
            <div className="space-y-3">
              <select
                value={selCategory}
                onChange={(e) => setSelCategory(e.target.value)}
                className="w-full bg-white/[0.06] border border-white/[0.1] rounded-xl p-3 text-white text-sm focus:ring-2 focus:ring-[#3BA4C5]/40 focus:border-[#3BA4C5]/40 appearance-none"
                data-testid="hero-category-select"
              >
                <option value="" className="text-slate-900">
                  {categories.length === 0 ? 'Loading categories…' : 'All categories'}
                </option>
                {categories.map(c => (
                  <option key={c.slug} value={c.slug} className="text-slate-900">{catName(c)}</option>
                ))}
              </select>

              <select
                value={selTreatment}
                onChange={(e) => setSelTreatment(e.target.value)}
                className="w-full bg-white/[0.06] border border-white/[0.1] rounded-xl p-3 text-white text-sm focus:ring-2 focus:ring-[#3BA4C5]/40 focus:border-[#3BA4C5]/40 appearance-none"
                data-testid="hero-treatment-select"
              >
                <option value="" className="text-slate-900">
                  {treatments.length === 0
                    ? 'Loading treatments…'
                    : filteredTreatments.length === 0
                      ? 'No treatments in this category'
                      : 'Select treatment…'}
                </option>
                {filteredTreatments.map(t => {
                  const slug = t.slug || t.treatment_id;
                  const name = trtName(t);
                  if (!slug || !name) return null;
                  return <option key={slug} value={slug} className="text-slate-900">{name}</option>;
                })}
              </select>

              <div className="grid grid-cols-2 gap-3">
                <select
                  value={selCountry}
                  onChange={(e) => setSelCountry(e.target.value)}
                  className="bg-white/[0.06] border border-white/[0.1] rounded-xl p-3 text-white text-sm"
                  data-testid="hero-country-select"
                >
                  <option value="" className="text-slate-900">Any country</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code} className="text-slate-900">{c.name}</option>
                  ))}
                </select>
                <select
                  value={selBudget}
                  onChange={(e) => setSelBudget(e.target.value)}
                  className="bg-white/[0.06] border border-white/[0.1] rounded-xl p-3 text-white text-sm"
                  data-testid="hero-budget-select"
                >
                  <option value="" className="text-slate-900">Any budget</option>
                  <option value="0-2000" className="text-slate-900">Under &euro;2,000</option>
                  <option value="2000-5000" className="text-slate-900">&euro;2,000 - &euro;5,000</option>
                  <option value="5000+" className="text-slate-900">&euro;5,000+</option>
                </select>
              </div>
              <Button
                onClick={onExplore}
                className="w-full bg-[#3BA4C5] hover:bg-[#2d8aab] text-white py-3 rounded-xl font-medium"
                data-testid="hero-search-btn"
              >
                <Search className="h-4 w-4 mr-2" /> Explore Clinics
              </Button>
              <p className="text-[11px] text-slate-500 text-center">No registration required</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Session ID for analytics
const getSessionId = () => {
  let sid = sessionStorage.getItem('treatx_session_id');
  if (!sid) {
    sid = Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('treatx_session_id', sid);
  }
  return sid;
};

const trackSponsoredEvent = (eventType, placementId, clinicId, extra = {}) => {
  api.post('/analytics/sponsored-event', {
    event_type: eventType,
    placement_id: placementId,
    clinic_id: clinicId,
    session_id: getSessionId(),
    ...extra,
  }).catch(() => {});
};

// 2. Sponsored Campaign Carousel (from /api/public/homepage-sponsored)
const SponsoredCampaignCarousel = ({ placements, title, subtitle, navigate }) => {
  const scrollRef = useRef(null);
  const impressionTracked = useRef(false);
  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  // Track impressions once on mount
  useEffect(() => {
    if (placements.length > 0 && !impressionTracked.current) {
      impressionTracked.current = true;
      placements.forEach((p, idx) => trackSponsoredEvent('sponsored_impression', p.placement_id, p.clinic_id, {
        treatment: p.treatment, priority_level: p.priority_level, slot: `slot_${idx + 1}`,
      }));
    }
  }, [placements]);

  if (!placements.length) return null;

  const handleClick = (p) => {
    trackSponsoredEvent('sponsored_click', p.placement_id, p.clinic_id, {
      treatment: p.treatment, priority_level: p.priority_level,
    });
    navigate(p.destination || '/find-clinics');
  };

  return (
    <section className="py-10 bg-slate-50" data-testid="sponsored-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-[#122033]">{title || 'Featured Clinics'}</h2>
            <p className="text-xs text-slate-400">{subtitle || 'Promoted treatments from verified providers'}</p>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => scroll(-1)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white transition-colors" data-testid="carousel-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => scroll(1)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white transition-colors" data-testid="carousel-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {placements.map((p, idx) => (
            <div key={p.placement_id} className="flex-none w-[310px] snap-start" data-testid={`sponsored-card-${p.placement_id}`}>
              <div className={`bg-gradient-to-br ${GRADIENT_COLORS[idx % GRADIENT_COLORS.length]} rounded-xl p-5 h-full text-white relative overflow-hidden cursor-pointer transition-transform hover:scale-[1.02]`}
                onClick={() => handleClick(p)}>
                <Badge className="bg-white/20 text-white text-[9px] border-0 mb-3" data-testid={`sponsored-label-${p.placement_id}`}>{p.sponsored_label || 'Sponsored'}</Badge>
                <h3 className="text-base font-bold mb-1">{p.title || p.treatment}</h3>
                <p className="text-sm text-white/80 mb-3 line-clamp-2">{p.subtitle}</p>
                <div className="flex items-end justify-between">
                  <div>
                    {p.price_from > 0 && (
                      <>
                        <span className="text-xs text-white/60">From</span>
                        <div className="text-xl font-bold">&euro;{(p.price_from || 0).toLocaleString()}</div>
                      </>
                    )}
                  </div>
                  <span className="text-[10px] text-white/60 max-w-[120px] text-right">{p.clinic_name}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 3. Trending Treatments Grid
const TrendingTreatmentsGrid = ({ treatments, title, subtitle, navigate }) => {
  if (!treatments.length) return null;

  const enriched = treatments.map((t, i) => ({
    ...t,
    treatment_name: typeof t.treatment_name === 'object' ? (t.treatment_name?.en || '') : (t.treatment_name || ''),
    description: typeof t.description === 'object' ? (t.description?.en || '') : (t.description || ''),
    Icon: ICON_MAP[t.icon] || Star,
    color: TREATMENT_COLORS[i % TREATMENT_COLORS.length],
  }));

  return (
    <section className="py-12 bg-white" data-testid="trending-treatments-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-[#122033] mb-1">{title || 'Trending Treatments'}</h2>
          <p className="text-sm text-slate-400">{subtitle || 'Most sought-after medical procedures worldwide'}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {enriched.map(t => (
            <button key={t.treatment_id} onClick={() => navigate(`/find-clinics?treatment=${t.slug || t.treatment_id}`)}
              className={`${t.color} border rounded-xl p-5 text-center transition-all hover:shadow-md hover:-translate-y-0.5 group`}
              data-testid={`trending-${t.treatment_id}`}>
              <t.Icon className="h-7 w-7 mx-auto mb-2.5 transition-transform group-hover:scale-110" />
              <h3 className="text-sm font-semibold mb-0.5">{t.treatment_name}</h3>
              <p className="text-[10px] opacity-70">{t.description}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// 4. Top Trusted Clinics Grid
const TrustedClinicsGrid = ({ clinics, title, subtitle, navigate }) => {
  if (!clinics.length) return null;

  return (
    <section className="py-12 bg-slate-50" data-testid="trusted-clinics-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#122033] mb-1">{title || 'Top Trusted Clinics'}</h2>
            <p className="text-sm text-slate-400">{subtitle || 'Highest trust scores on the platform'}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/find-clinics')} className="text-xs gap-1">
            View All <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {clinics.map((c, idx) => (
            <Card key={c.clinic_id} className="border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/clinics/${c.clinic_id}`)}
              data-testid={`trusted-clinic-${c.clinic_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-xl bg-[#122033] flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#122033] line-clamp-1">{c.clinic_name}</h3>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" />{c.country}</span>
                    </div>
                  </div>
                  <div className="bg-emerald-50 rounded-lg px-2.5 py-1 text-center">
                    <div className="text-lg font-bold text-emerald-600">{c.trust_score}</div>
                    <div className="text-[8px] text-emerald-500 font-medium">TRUST</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-[#122033]">{c.years_experience || 0}y</div>
                    <div className="text-[9px] text-slate-400">Experience</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-[#122033]">{c.procedures_performed > 1000 ? `${(c.procedures_performed / 1000).toFixed(1)}k` : c.procedures_performed}</div>
                    <div className="text-[9px] text-slate-400">Procedures</div>
                  </div>
                </div>
                {c.accreditations?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {c.accreditations.map(a => (
                      <Badge key={a} variant="outline" className="text-[9px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-100">{a}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// 5. Most Chosen Clinics Grid
const MostChosenClinicsGrid = ({ clinics, title, subtitle, navigate }) => {
  if (!clinics.length) return null;

  return (
    <section className="py-12 bg-white" data-testid="most-chosen-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#122033] mb-1">{title || 'Most Chosen Clinics'}</h2>
            <p className="text-sm text-slate-400">{subtitle || 'Where patients book the most'}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/find-clinics')} className="text-xs gap-1">
            View All <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {clinics.map(c => (
            <Card key={c.clinic_id} className="border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/clinics/${c.clinic_id}`)}
              data-testid={`chosen-clinic-${c.clinic_id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#3BA4C5] to-[#2d8aab] flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#122033]">{c.clinic_name}</h3>
                    <span className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" />{c.country}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Offers received</span>
                    <span className="text-sm font-bold text-[#122033]">{c.quotes_received}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Offers accepted</span>
                    <span className="text-sm font-bold text-[#122033]">{c.offers_accepted}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Completion rate</span>
                    <span className="text-sm font-bold text-emerald-600">{c.payment_completion_rate}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#3BA4C5] to-emerald-400 rounded-full transition-all" style={{ width: `${c.payment_completion_rate}%` }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// 6. Why Trust TreatX Section
const TrustSection = ({ items, title, subtitle, language }) => {
  if (!items.length) return null;
  return (
    <section className="py-12 bg-[#0a1628] text-white" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-1">{title || 'Why Trust TreatX'}</h2>
          <p className="text-sm text-slate-400">{subtitle || 'Your safety and confidence are our top priority'}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {items.map((item, idx) => {
            const IconComp = TRUST_ICON_MAP[item.icon] || ShieldCheck;
            return (
              <div key={idx} className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-6 text-center" data-testid={`trust-item-${idx}`}>
                <div className="h-12 w-12 rounded-xl bg-[#3BA4C5]/10 flex items-center justify-center mx-auto mb-4">
                  <IconComp className="h-6 w-6 text-[#3BA4C5]" />
                </div>
                <h3 className="text-base font-semibold mb-2">{getLocalizedField(item.title, language)}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{getLocalizedField(item.description, language)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// 7. Certifications Section (canonical). Reads exclusively from
// `treatx_corporate_certificates` via `<CorporateCertificates/>`. Legacy
// `landing-content.certifications` is intentionally NOT consumed here —
// there is one public certificate authority across the platform.
const CertificationsSection = ({ certs }) => {
  const { t } = useTranslation();
  if (!Array.isArray(certs) || certs.length === 0) return null;
  const labels = {
    grantedHeading: t('corporateIdentity.certGrantedHeading', 'Certified & Authorized'),
    grantedCaption: t('corporateIdentity.certGrantedCaption', 'Certificates currently held by the company.'),
    pendingHeading: t('corporateIdentity.certPendingHeading', 'Authorizations in Progress'),
    pendingCaption: t('corporateIdentity.certPendingCaption', 'These authorizations have been applied for and are not yet granted.'),
    pending: t('corporateIdentity.pending', 'Application in progress'),
    clickToEnlarge: t('corporateIdentity.clickToEnlarge', 'Click to enlarge'),
    lightbox: {
      pending: t('corporateIdentity.pending', 'Application in progress'),
      openPdf: t('corporateIdentity.openPdf', 'Open PDF'),
      pdfNotice: t('corporateIdentity.pdfNotice', 'This certificate is only available as a PDF document.'),
      noAsset: t('corporateIdentity.noAsset', 'A copy of this certificate has not been published yet.'),
      noAssetPending: t('corporateIdentity.noAssetPending', 'The certificate document will be published here once the authorization is granted.'),
      certNumber: t('corporateIdentity.certNumber', 'Certificate no.'),
      scope: t('corporateIdentity.scope', 'Scope'),
      issueDate: t('corporateIdentity.issueDate', 'Issued'),
      validUntil: t('corporateIdentity.validUntil', 'Valid until'),
    },
  };
  return (
    <section
      className="py-10 bg-white border-b"
      data-testid="corporate-certificates-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">
            {t('corporateIdentity.homeHeading', 'Certifications & Authorizations')}
          </h2>
          <p className="text-sm text-slate-600 max-w-2xl mx-auto">
            {t('corporateIdentity.homeSubheading', 'What we hold today, and what we have applied for.')}
          </p>
        </div>
        <CorporateCertificates certificates={certs} variant="homepage" labels={labels} />
      </div>
    </section>
  );
};

// 8. How It Works Section
const HowItWorks = ({ steps, title, subtitle }) => (
  <section className="py-12 bg-slate-50" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#122033] mb-1">{title || 'How It Works'}</h2>
        <p className="text-sm text-slate-400">{subtitle || 'Four simple steps to world-class care'}</p>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        {steps.map((step, idx) => (
          <div key={step.step_number} className="relative text-center" data-testid={`step-${step.step_number}`}>
            {idx < steps.length - 1 && (
              <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-slate-200" />
            )}
            <div className="relative z-10">
              <div className="h-14 w-14 rounded-2xl bg-[#122033] text-white flex items-center justify-center mx-auto mb-3 shadow-lg">
                <step.Icon className="h-6 w-6" />
              </div>
              <span className="text-[10px] font-bold text-[#3BA4C5] uppercase tracking-wider">Step {step.step_number}</span>
              <h3 className="text-sm font-semibold text-[#122033] mt-1 mb-1">{step.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// 9. Testimonials Section
const TestimonialsSection = ({ testimonials, title, subtitle }) => (
  <section className="py-12 bg-white" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#122033] mb-1">{title || 'Patient Stories'}</h2>
        <p className="text-sm text-slate-400">{subtitle || 'Hear from patients who trusted TreatX'}</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {testimonials.map((t, idx) => (
          <Card key={idx} className="border border-slate-200" data-testid={`testimonial-${idx}`}>
            <CardContent className="p-5">
              <div className="flex gap-0.5 mb-3">
                {[...Array(t.rating)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#3BA4C5] to-[#2d8aab] flex items-center justify-center text-white text-xs font-bold">
                  {t.name[0]}
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#122033]">{t.name}</p>
                  <p className="text-[10px] text-slate-400">{t.treatment} — {t.country}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// 10. Final CTA Section
const FinalCTA = ({ navigate }) => (
  <section className="py-14 bg-[#0a1628] text-white" data-testid="final-cta-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
      <h2 className="text-3xl font-bold mb-3">Ready to Start Your Journey?</h2>
      <p className="text-slate-400 text-sm max-w-lg mx-auto mb-8">
        Join thousands of patients who found world-class care at a fraction of the cost through TreatX.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <Button onClick={() => navigate('/find-clinics')} className="bg-[#3BA4C5] hover:bg-[#2d8aab] text-white px-8 py-3 rounded-xl" data-testid="cta-find-clinics">
          Find Clinics <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <Button variant="outline" onClick={() => navigate('/treatments')} className="border-white/20 text-white hover:bg-white/5 px-8 py-3 rounded-xl" data-testid="cta-treatments">
          Browse Treatments
        </Button>
      </div>
    </div>
  </section>
);


// ═══════════════════════════════════════════════════════════
// QUICK SIGNUP MODAL
// ═══════════════════════════════════════════════════════════

const QuickSignupModal = ({ open, onClose, actionLabel }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = useRef([]);
  const { content: tosConsentContent } = useContent('login_tos_consent_text');

  // TREATX-O89: resend cooldown mirrors the server's 60s budget.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const requestCode = async ({ resend = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post(`/auth/quick-signup`, { email });
      setCooldown(res.data?.resend_after || 60);
      setOtp(['', '', '', '', '', '']);
      setStep('verifying');
      if (resend) setError('');
    } catch (e) {
      setError(apiErrorMessage(e, t('quickSignup.sendFailed', 'We could not send the code. Please try again.')));
      if (resend) setCooldown(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = () => requestCode();

  const handleVerifyOtp = async () => {
    setLoading(true);
    setError('');
    const otpStr = otp.join('');
    try {
      const res = await api.post(`/auth/quick-signup/verify`, { email, otp: otpStr });
      const data = res.data;
      if (data.success || data.user) {
        // SECURITY-S4: canonical unified surface only.
        localStorage.setItem('treatx_unified_user', JSON.stringify({
          ...data.patient,
          role: 'patient',
        }));
      }
      setStep('done');
    } catch (e) {
      setError(apiErrorMessage(e, t('quickSignup.verifyFailed', 'That code did not work. Please try again.')));
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[0];
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="quick-signup-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-[#122033]">
              {step === 'email'
                ? t('quickSignup.title', 'Quick Sign Up')
                : step === 'verifying'
                  ? t('quickSignup.verifyTitle', 'Verify Email')
                  : t('quickSignup.doneTitle', 'All Set!')}
            </h3>
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="signup-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>

          {error && <p className="text-xs text-red-500 mb-3" data-testid="signup-error">{error}</p>}

          {step === 'email' && (
            <>
              <p className="text-sm text-slate-500 mb-4">{t('quickSignup.emailPrompt', "Enter your email and we'll send you a one-time code.")}</p>
              <div className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                    className="pl-10" data-testid="signup-email-input" />
                </div>
                <Button className="w-full bg-[#3BA4C5] hover:bg-[#2d8aab]" onClick={handleSendOtp}
                  disabled={!email.includes('@') || loading} data-testid="signup-continue-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('quickSignup.continue', 'Continue')}
                </Button>
                <p className="text-[10px] text-slate-400 text-center">{tosConsentContent?.body || 'Loading...'}</p>
              </div>
            </>
          )}

          {step === 'verifying' && (
            <>
              <p className="text-sm text-slate-500 mb-4">
                {t('quickSignup.codeSent', "We've sent a 6-digit code to")} <strong>{email}</strong>
              </p>
              <div className="flex gap-2 justify-center mb-4">
                {otp.map((digit, i) => (
                  <input key={i} ref={el => otpRefs.current[i] = el} maxLength={1} value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    className="w-10 h-12 text-center text-xl font-bold border-2 border-slate-200 rounded-lg focus:border-[#3BA4C5] focus:ring-1 focus:ring-[#3BA4C5]"
                    data-testid={`otp-input-${i}`} />
                ))}
              </div>
              <Button className="w-full bg-[#3BA4C5] hover:bg-[#2d8aab]" onClick={handleVerifyOtp}
                disabled={otp.join('').length < 6 || loading} data-testid="signup-verify-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('quickSignup.verify', 'Verify')}
              </Button>
              <button
                type="button"
                onClick={() => requestCode({ resend: true })}
                disabled={loading || cooldown > 0}
                className="w-full mt-3 text-xs text-slate-500 hover:text-[#3BA4C5] disabled:text-slate-300 transition-colors"
                data-testid="signup-resend-btn"
              >
                {cooldown > 0
                  ? t('quickSignup.resendIn', 'Resend code in {{seconds}}s', { seconds: cooldown })
                  : t('quickSignup.resend', 'Resend code')}
              </button>
            </>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-7 w-7 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-600 mb-4">{t('quickSignup.done', 'Your account is ready!')}</p>
              <Button className="w-full bg-[#3BA4C5] hover:bg-[#2d8aab]" onClick={onClose} data-testid="signup-done-btn">
                {t('quickSignup.continue', 'Continue')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════
// MAIN PAGE — DATA FETCHING & SECTION RENDERING
// ═══════════════════════════════════════════════════════════

export default function LandingPageV2() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [showSignup, setShowSignup] = useState(false);
  const [signupAction, setSignupAction] = useState('');
  const [loading, setLoading] = useState(true);

  // Data state
  const [sectionsConfig, setSectionsConfig] = useState([]);
  const [sponsoredPlacements, setSponsoredPlacements] = useState([]);
  const [trendingTreatments, setTrendingTreatments] = useState([]);
  const [trustedClinics, setTrustedClinics] = useState([]);
  const [mostChosenClinics, setMostChosenClinics] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [trustContent, setTrustContent] = useState([]);
  // TREATX-OWNER-ACCEPTANCE-P0-INVITE-LOGIN-SEARCH-01 — dynamic hero
  // search dropdown data. Countries derive from the canonical
  // active-provider catalog (with ISO fallback); treatments derive
  // from the canonical active-provider-treatments catalog (with the
  // existing trendingTreatments list as fallback).
  const [searchCountries, setSearchCountries] = useState([]);
  const [searchTreatments, setSearchTreatments] = useState([]);
  // TREATX-SSOT-PART4 — canonical Master Medical Catalog categories.
  const [searchCategories, setSearchCategories] = useState([]);
  const [heroMetrics, setHeroMetrics] = useState({
    total_patients: 5000, total_clinics: 200, total_countries: 30, average_rating: 4.8,
  });

  // CMS content for legal claims on landing page
  const { content: trustProtectionContent } = useContent('trust_patient_protection_text');
  const { content: certGdprContent } = useContent('cert_gdpr_badge_text');
  const { content: tosConsentContent } = useContent('login_tos_consent_text');

  const fetchData = useCallback(async () => {
    try {
      // Fetch organic data from unified discovery + CMS + sponsored (in parallel)
      const [discoveryRes, contentRes, sponsoredRes] = await Promise.all([
        api.get(`/discover/clinics?mode=homepage&language=${language}`),
        api.get(`/public/landing-content`),
        api.get(`/public/homepage-sponsored`),
      ]);

      if (discoveryRes.ok) {
        const discovery = discoveryRes.data;
        setSectionsConfig(discovery.sections_config || []);
        setTrendingTreatments(discovery.trending_treatments || []);
        setTrustedClinics(discovery.trusted_clinics || []);
        setMostChosenClinics(discovery.most_chosen_clinics || []);
      }

      if (sponsoredRes.ok) {
        const sponsored = sponsoredRes.data;
        setSponsoredPlacements(sponsored.placements || []);
      }

      if (contentRes.ok) {
        const content = contentRes.data;
        // Canonical Corporate Certificates (SSOT: treatx_corporate_certificates)
        // is fetched separately below. Legacy `content.certifications` is
        // deliberately NOT consumed here — there is one public certificate
        // authority. Admin CMS records under /admin/landing-content are
        // preserved server-side.

        // Trust statements from CMS
        const trustBlocks = content.trust_statements || [];
        if (trustBlocks.length > 0) {
          setTrustContent(trustBlocks.map(b => ({
            title: b.title, description: b.description, icon: 'ShieldCheck', image_url: b.image_url,
          })));
        } else if (content.trust_content && content.trust_content.length > 0) {
          setTrustContent(content.trust_content.map(b => ({
            title: b.title, description: b.description, icon: 'ShieldCheck', image_url: b.image_url,
          })));
        } else {
          // Build trust fallback with CMS content
          const trustFallback = TRUST_FALLBACK.map(item => {
            if (item.cmsKey === 'trust_patient_protection_text' && trustProtectionContent) {
              return { ...item, description: trustProtectionContent.body };
            }
            return item;
          });
          setTrustContent(trustFallback);
        }
      }

      // TREATX-SSOT-PART4 — hero-search data comes from the canonical
      // Master Medical Catalog. Treatments are filtered by
      // `status=published AND isActive=true` server-side. Provider
      // enablement is NOT applied to the selector — provider
      // availability is a separate concept surfaced only after the
      // user performs a search on /find-clinics.
      try {
        const [countriesRes, treatmentsRes, categoriesRes] = await Promise.all([
          api.get('/public/catalog/countries-full').catch(() => null),
          api.get('/public/catalog/treatments').catch(() => null),
          api.get('/public/catalog/categories').catch(() => null),
        ]);
        const countries = Array.isArray(countriesRes?.data?.countries)
          ? countriesRes.data.countries : [];
        setSearchCountries(countries);
        const canonicalTreatments = Array.isArray(treatmentsRes?.data?.treatments)
          ? treatmentsRes.data.treatments : [];
        setSearchTreatments(canonicalTreatments);
        const canonicalCategories = Array.isArray(categoriesRes?.data?.categories)
          ? categoriesRes.data.categories.filter(c => (c.status || 'active') === 'active')
          : [];
        setSearchCategories(canonicalCategories);
      } catch (_err) {
        // Silent — fall through to defaults set during normal load.
      }

      // Canonical Corporate Certificates (SSOT). Silent fallback on
      // error → NO certificates rendered (never fabricated defaults).
      try {
        const ccRes = await corporateIdentityAPI
          .listCertificates({ surface: 'homepage' })
          .catch(() => null);
        const list = ccRes?.data?.certificates;
        if (Array.isArray(list)) setCertifications(list);
      } catch (_err) { /* silent */ }
    } catch (err) {
      console.error('[LandingV2] Fetch error:', err);
      // Certificates: legacy fallback removed — a fetch failure means
      // the section renders empty rather than a fabricated set.
      const trustFallback = TRUST_FALLBACK.map(item => {
        if (item.cmsKey === 'trust_patient_protection_text' && trustProtectionContent) {
          return { ...item, description: trustProtectionContent.body };
        }
        return item;
      });
      setTrustContent(trustFallback);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build section lookup: key -> config
  const sectionMap = {};
  sectionsConfig.forEach(s => { sectionMap[s.section_key] = s; });

  const isEnabled = (key) => {
    const cfg = sectionMap[key];
    return cfg ? cfg.enabled !== false : true;
  };

  const getTitle = (key, fallback) => sectionMap[key]?.title_override || fallback;
  const getSubtitle = (key, fallback) => sectionMap[key]?.subtitle_override || fallback;

  // Sort sections by display_order
  const orderedSections = [...sectionsConfig].sort((a, b) => a.display_order - b.display_order);

  // TREATX-SEO-RECOVERY-S1 — shared homepage head authority (see homepageSeo.js).
  // V2 previously emitted no metadata at all, so `/v2` was indexable with the
  // shell's generic title and no canonical.
  const homepageSeo = useHomepageSeoProps();

  if (loading) {
    return (
      <div className="min-h-screen bg-white" data-testid="landing-page-v2-loading">
        <SectionSkeleton dark />
        <SectionSkeleton />
        <SectionSkeleton />
      </div>
    );
  }

  // TREATX-SSOT-PART4 — hero-search treatments come exclusively from
  // the canonical Master Medical Catalog. Provider enablement is a
  // separate concept surfaced on /find-clinics, not in the selector.
  const heroTreatments = searchTreatments || [];

  // Render section by key
  const renderSection = (key) => {
    if (!isEnabled(key)) return null;

    switch (key) {
      case 'hero':
        return <HeroSearch key={key} metrics={heroMetrics} treatments={heroTreatments} categories={searchCategories} countries={searchCountries} navigate={navigate} />;
      case 'sponsored_campaigns':
        return <SponsoredCampaignCarousel key={key} placements={sponsoredPlacements}
          title={getTitle('sponsored_campaigns', 'Featured Clinics')}
          subtitle={getSubtitle('sponsored_campaigns', 'Promoted treatments from verified providers')}
          navigate={navigate} />;
      case 'trending_treatments':
        return <TrendingTreatmentsGrid key={key} treatments={trendingTreatments}
          title={getTitle('trending_treatments', 'Trending Treatments')}
          subtitle={getSubtitle('trending_treatments', 'Most sought-after medical procedures worldwide')}
          navigate={navigate} />;
      case 'trusted_clinics':
        return <TrustedClinicsGrid key={key} clinics={trustedClinics}
          title={getTitle('trusted_clinics', 'Top Trusted Clinics')}
          subtitle={getSubtitle('trusted_clinics', 'Highest trust scores on the platform')}
          navigate={navigate} />;
      case 'most_chosen_clinics':
        return <MostChosenClinicsGrid key={key} clinics={mostChosenClinics}
          title={getTitle('most_chosen_clinics', 'Most Chosen Clinics')}
          subtitle={getSubtitle('most_chosen_clinics', 'Where patients book the most')}
          navigate={navigate} />;
      case 'trust_content':
        return <TrustSection key={key} items={trustContent}
          title={getTitle('trust_content', 'Why Trust TreatX')}
          subtitle={getSubtitle('trust_content', 'Your safety and confidence are our top priority')}
          language={language} />;
      case 'certifications':
        return <CertificationsSection key={key} certs={certifications} />;
      case 'how_it_works':
        return <HowItWorks key={key} steps={HOW_IT_WORKS}
          title={getTitle('how_it_works', 'How It Works')}
          subtitle={getSubtitle('how_it_works', 'Four simple steps to world-class care')} />;
      case 'testimonials':
        return <TestimonialsSection key={key} testimonials={TESTIMONIALS}
          title={getTitle('testimonials', 'Patient Stories')}
          subtitle={getSubtitle('testimonials', 'Hear from patients who trusted TreatX')} />;
      case 'final_cta':
        return <FinalCTA key={key} navigate={navigate} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-v2">
      <SEO {...homepageSeo} />
      {orderedSections.length > 0
        ? orderedSections.map(s => renderSection(s.section_key))
        : (
          <>
            <HeroSearch metrics={heroMetrics} treatments={heroTreatments} categories={searchCategories} countries={searchCountries} navigate={navigate} />
            <TrustSection items={trustContent} language={language} />
            <CertificationsSection certs={certifications} />
            <HowItWorks steps={HOW_IT_WORKS} />
            <TestimonialsSection testimonials={TESTIMONIALS} />
            <FinalCTA navigate={navigate} />
          </>
        )
      }
      <QuickSignupModal open={showSignup} onClose={() => setShowSignup(false)} actionLabel={signupAction} />
      <LegalFooter className="mt-16" />
    </div>
  );
}
